import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Box,
  Chip,
  Autocomplete,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import axios from "axios";
import toast from "react-hot-toast";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import MenuIcon from "@mui/icons-material/Menu";
import Loader from "../../components/loader/Loader";
import * as MuiIcons from "@mui/icons-material";
import { mainMenu } from "../../layout/sideNav/sideNav";
import SaveIcon from "@mui/icons-material/Save";

import {
  useTablePagination,
  useTableHeight,
} from "../../hooks/useTablePagination";

// 🔹 Extract all routes from mainMenu
const extractRoutes = (menuItems) => {
  const routes = [];

  menuItems.forEach((item) => {
    // Add parent route if exists
    if (item.route) {
      routes.push(item.route);
    }

    // Add child routes if exists
    if (item.items && item.items.length > 0) {
      item.items.forEach((subItem) => {
        if (subItem.route) {
          routes.push(subItem.route);
        }
      });
    }
  });

  // Remove duplicates and sort
  return [...new Set(routes)].sort();
};

const availableRoutes = extractRoutes(mainMenu);

const MenuCreate = () => {
  const [menuList, setMenuList] = useState([]);
  const [loader, setLoader] = useState(false);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [parentMenusFromAPI, setParentMenusFromAPI] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    menu_name: "",
    parent_menu_id: "",
    route: "",
    icon: "",
    // permission_id: "",
  });
  const {
      page,
      rowsPerPage,
      searchText,
      onChangePage,
      onChangeRowsPerPage,
      onSearchChange,
    } = useTablePagination("menucreate", 10);
  
  const [errors, setErrors] = useState({});
  const tableHeight = useTableHeight();
  const ICON_VARIANT_PRIORITY = ["", "Outlined", "Rounded", "Sharp", "TwoTone"];

  // 🔹 Get all MUI Icon names
  // const getAllMuiIcons = () => {
  //   const iconNames = Object.keys(MuiIcons)
  //     .filter((key) => key !== "default" && typeof MuiIcons[key] === "object")
  //     .sort();
  //   return iconNames;
  // };
  const getBaseIconName = (iconName) => {
  return iconName
    .replace(/Outlined|Rounded|Sharp|TwoTone$/, "");
};
const getFilteredMuiIcons = () => {
  const iconNames = Object.keys(MuiIcons).filter(
    (key) => key !== "default" && typeof MuiIcons[key] === "object"
  );

  const iconMap = {};

  iconNames.forEach((iconName) => {
    const baseName = getBaseIconName(iconName);

    const currentVariant = ICON_VARIANT_PRIORITY.findIndex((v) =>
      iconName.endsWith(v)
    );

    if (!iconMap[baseName]) {
      iconMap[baseName] = iconName;
    } else {
      const existingVariant = ICON_VARIANT_PRIORITY.findIndex((v) =>
        iconMap[baseName].endsWith(v)
      );

      // pick higher priority icon
      if (currentVariant < existingVariant) {
        iconMap[baseName] = iconName;
      }
    }
  });

  return Object.values(iconMap).sort();
};


  // const muiIconList = getAllMuiIcons();
  const muiIconList = getFilteredMuiIcons();


  // 🔹 Get Icon Component by name
  const getIconComponent = (iconName) => {
    if (!iconName || !MuiIcons[iconName]) return null;
    return MuiIcons[iconName];
  };

  // 🔹 Fetch Menus
  const loadMenus = async () => {
    try {
      setLoader(true);
      const res = await axios.get("/menuItems");
      const menus = res?.data?.data || [];

      // Extract unique parent menus
      // A parent menu is one that appears in other menus' parent_menu object
      const parentMenuIds = new Set();
      const parentMenusMap = new Map();

      menus.forEach((menu) => {
        if (menu.parent_menu && menu.parent_menu.parent_menu_id) {
          parentMenuIds.add(menu.parent_menu.parent_menu_id);
          if (!parentMenusMap.has(menu.parent_menu.parent_menu_id)) {
            parentMenusMap.set(menu.parent_menu.parent_menu_id, {
              menu_id: menu.parent_menu.parent_menu_id,
              menu_name: menu.parent_menu.parent_menu_name,
            });
          }
        }
      });

      const parentMenusList = Array.from(parentMenusMap.values());

      setParentMenusFromAPI(parentMenusList);
      setMenuList(menus);
      console.log("All menus:", menus);
      console.log("Parent menus:", parentMenusList);
    } catch (error) {
      toast.error("Failed to fetch menus");
      console.error("Error loading menus:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    loadMenus();
  }, []);

  // 🔹 Validate Form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.menu_name.trim()) {
      newErrors.menu_name = "Menu name is required";
    }

    // if (!formData.permission_id.trim()) {
    //   newErrors.permission_id = "Permission ID is required";
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔹 Handle Input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  // 🔹 Handle Icon Change
  const handleIconChange = (event, newValue) => {
    setFormData({ ...formData, icon: newValue || "" });
    if (errors.icon) {
      setErrors({ ...errors, icon: "" });
    }
  };

  // 🔹 Reset Form
  const resetForm = () => {
    setFormData({
      menu_name: "",
      parent_menu_id: "",
      route: "",
      icon: "",
      permission_id: "",
    });
    setErrors({});
    setEditMode(false);
    setSelectedMenu(null);
  };

  // 🔹 Open Create Dialog
  const handleOpenCreate = () => {
    resetForm();
    setOpen(true);
  };

  // 🔹 Open Edit Dialog
  const handleOpenEdit = (menu) => {
    setSelectedMenu(menu);
    setFormData({
      menu_name: menu.menu_name || "",
      parent_menu_id: menu.parent_menu_id || "",
      route: menu.route || "",
      icon: menu.icon || "",
      permission_id: menu.permission_id || "",
    });
    setEditMode(true);
    setOpen(true);
  };

  // 🔹 Close Dialog
  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  // 🔹 Create Menu
  const handleCreate = async () => {
    if (!validateForm()) {
      toast.error("Please fill all required fields");
      return;
    }

    if (isSubmitting) return; // Prevent duplicate submission

    try {
      setIsSubmitting(true);
      const payload = {
        menu_name: formData.menu_name,
        route: formData.route || null,
        icon: formData.icon || null,
        permission_id: formData.permission_id,
        // If parent_menu_id is empty string or falsy, set to null (root menu)
        // Otherwise use the selected parent_menu_id
        parent_menu_id: formData.parent_menu_id || null,
      };

      console.log("Creating menu with payload:", payload);

      await axios.post("/menuItems", payload);
      toast.success(
        formData.parent_menu_id
          ? "Submenu created successfully"
          : "Parent menu created successfully",
      );
      setOpen(false);
      resetForm();
      loadMenus();
    } catch (error) {
      toast.error(error.response?.data?.message || "Menu creation failed");
      console.error("Error creating menu:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 🔹 Update Menu
  const handleUpdate = async () => {
    if (!validateForm()) {
      toast.error("Please fill all required fields");
      return;
    }

    if (isSubmitting) return; // Prevent duplicate submission

    try {
      setIsSubmitting(true);
      const payload = {
        ...formData,
        parent_menu_id: formData.parent_menu_id || null,
      };

      await axios.put(`/menuItems/${selectedMenu.menu_id}`, payload);
      toast.success("Menu updated successfully");
      setOpen(false);
      resetForm();
      loadMenus();
    } catch (error) {
      toast.error(error.response?.data?.message || "Menu update failed");
      console.error("Error updating menu:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 🔹 Handle Submit (Create or Update)
  const handleSubmit = () => {
    if (editMode) {
      handleUpdate();
    } else {
      handleCreate();
    }
  };

  // 🔹 Open Delete Confirmation
  const handleOpenDelete = (menu) => {
    setSelectedMenu(menu);
    setDeleteDialog(true);
  };

  // 🔹 Delete Menu
  const handleDelete = async () => {
    try {
      await axios.delete(`/menuItems/${selectedMenu.menu_id}`);
      toast.success("Menu deleted successfully");
      setDeleteDialog(false);
      setSelectedMenu(null);
      loadMenus();
    } catch (error) {
      toast.error(error.response?.data?.message || "Menu deletion failed");
      console.error("Error deleting menu:", error);
    }
  };

  // 🔹 Get Dialog Title
  const getDialogTitle = () => {
    if (editMode) return "Edit Menu";
    return "Create Menu";
  };

  // 🔹 Table Columns
  const columns = [
    {
      name: "menu_id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => dataIndex + 1,
        filter: false,
      },
    },
    {
      name: "menu_name",
      label: "Menu Name",
      options: {
        filter: true,
      },
    },
    {
      name: "parent_menu_id",
      label: "Parent Menu",
      options: {
        filter: true,
        customBodyRender: (value, tableMeta) => {
          const menu = menuList[tableMeta.rowIndex];

          if (!menu) return "—";
          if (!menu.parent_menu) return "—";

          return menu.parent_menu.parent_menu_name || "—";
        },
      },
    },
    {
      name: "route",
      label: "Route",
      options: {
        filter: false,
        customBodyRender: (value) => value || "—",
      },
    },

    {
      name: "icon",
      label: "Icon",
      options: {
        filter: false,
        customBodyRender: (value) => {
          if (!value) return "—";

          const IconComponent = getIconComponent(value);

          return IconComponent ? (
            <Tooltip title={value} arrow placement="top">
              <Box
                sx={{
                  // display: "flex",
                  // alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <IconComponent fontSize="small" />
              </Box>
            </Tooltip>
          ) : (
            value
          );
        },
      },
    },
    {
      name: "menu_id",
      label: "Actions",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const menu = menuList[dataIndex];
          return (
            <Box>
              <Tooltip title="Edit Menu">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleOpenEdit(menu)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Menu">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleOpenDelete(menu)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    },
  ];
   const options = {
    selectableRows: false,
    filter: false,
    download: false,
    viewColumns: true,
    print: false,
    rowsPerPage: rowsPerPage,
    page: page,
    fixedHeader: true,
    tableBodyHeight: tableHeight,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
    searchText,
    textLabels: {
      body: {
        noMatch: loader ? "Loading..." : "No data available",
      },
    },
  };
  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <h4
              style={{
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
               Menu Management
            </h4>
          </Box>

          <MUIDataTable
            title={
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenCreate}
              >
                Add Menu
              </Button>
            }
            data={menuList}
            columns={columns}
            options={options}
          />

          {/* 🔹 Create/Edit Menu Dialog */}
          <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle className="text-primary">
              {getDialogTitle()}
            </DialogTitle>

            <DialogContent>
              <TextField
                fullWidth
                margin="dense"
                label="Menu Name *"
                name="menu_name"
                value={formData.menu_name}
                onChange={handleChange}
                error={!!errors.menu_name}
                helperText={errors.menu_name}
                autoFocus
              />

              {!editMode && (
                <TextField
                  select
                  fullWidth
                  margin="dense"
                  label="Parent Menu"
                  name="parent_menu_id"
                  value={formData.parent_menu_id}
                  onChange={handleChange}
                  helperText="Select a parent menu or leave empty to create root menu"
                >
                  <MenuItem value="">None (Create Root Menu)</MenuItem>
                  {parentMenusFromAPI.map((menu) => (
                    <MenuItem key={menu.menu_id} value={menu.menu_id}>
                      {menu.menu_name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {editMode && (
                <TextField
                  select
                  fullWidth
                  margin="dense"
                  label="Parent Menu"
                  name="parent_menu_id"
                  value={formData.parent_menu_id}
                  onChange={handleChange}
                  helperText="Select parent or leave as root menu"
                >
                  <MenuItem value="">None (Root Menu)</MenuItem>
                  {parentMenusFromAPI
                    .filter((menu) => menu.menu_id !== selectedMenu?.menu_id)
                    .map((menu) => (
                      <MenuItem key={menu.menu_id} value={menu.menu_id}>
                        {menu.menu_name}
                      </MenuItem>
                    ))}
                </TextField>
              )}

              {/* 🔹 Route Dropdown (Updated) */}
              <TextField
                select
                fullWidth
                margin="dense"
                label="Route"
                name="route"
                value={formData.route}
                onChange={handleChange}
                helperText="Select a route from available pages"
              >
                <MenuItem value="">None</MenuItem>
                {availableRoutes.map((route) => (
                  <MenuItem key={route} value={route}>
                    {route}
                  </MenuItem>
                ))}
              </TextField>

              {/* 🔹 Icon Autocomplete with Preview */}
              <Autocomplete
                fullWidth
                options={muiIconList}
                value={formData.icon}
                onChange={handleIconChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="dense"
                    label="Icon"
                    helperText="Search and select a Material-UI icon"
                    placeholder="Start typing icon name..."
                  />
                )}
                renderOption={(props, option) => {
                  const IconComponent = getIconComponent(option);
                  return (
                    <Box
                      component="li"
                      {...props}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      {IconComponent && <IconComponent fontSize="small" />}
                      <span>{option}</span>
                    </Box>
                  );
                }}
                sx={{ mt: 1 }}
              />

              {/* 🔹 Icon Preview */}
              {formData.icon && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: "1px dashed #ccc",
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "action.hover",
                  }}
                >
                  {getIconComponent(formData.icon) &&
                    React.createElement(getIconComponent(formData.icon), {
                      sx: { fontSize: 18, color: "primary.main" },
                    })}
                  <Box sx={{ fontSize: 12 }}>
                    <strong>Selected Icon:</strong> {formData.icon}
                  </Box>
                </Box>
              )}

              {/* <TextField
                fullWidth
                margin="dense"
                label="Permission ID *"
                name="permission_id"
                value={formData.permission_id}
                onChange={handleChange}
                error={!!errors.permission_id}
                helperText={
                  errors.permission_id ||
                  "Required permission to access this menu"
                }
              /> */}
            </DialogContent>

            <DialogActions>
              <Button onClick={handleClose} variant="contained" color="error">
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting}
                startIcon={<SaveIcon />}
              >
                {isSubmitting ? "Saving..." : editMode ? "Update" : "Save"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* 🔹 Delete Confirmation Dialog */}
          <Dialog
            open={deleteDialog}
            onClose={() => setDeleteDialog(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              Are you sure you want to delete the menu "
              <strong>{selectedMenu?.menu_name}</strong>"?
              {selectedMenu?.children?.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1,
                    bgcolor: "warning.light",
                    borderRadius: 1,
                  }}
                >
                  <strong>Warning:</strong> This menu has{" "}
                  {selectedMenu.children.length} submenu(s) that may be
                  affected.
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialog(false)} variant="contained">
                Cancel
              </Button>
              <Button onClick={handleDelete} variant="contained" color="error">
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
};

export default MenuCreate;
